import { EM_DASH } from '../lib/typography';
import { Component } from 'preact';
import { reportMatchOutcome } from '../utils/arena/matchReporting';
import { neutralArbitration } from '../utils/arena/arbitrationOutcomes';
import { uploadMatchLog } from '../utils/game/replay/logUpload';
import { cn } from '../lib/utils';
import {
  GOLD, GOLD_TEXT, COIN_COLOR, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  PANEL_BG, FourCorners,
} from '../lib/medievalTheme';
import { getLocalApiOrigin } from '../utils/localApi';
import { playUI, UI } from '../utils/arena/uiSounds';
import { formatRank, TIER_HEX } from '../utils/arena/rankUtils';
import { CoinIcon, ShardIcon, IconSwords, IconLaurels, IconSeasonScroll } from './ui/icons';
import { Tooltip } from './ui/tooltip';
import VictoryEmbers from './VictoryEmbers';
import OracleResult from './play/OracleResult';
import BattlemageResult from './play/BattlemageResult';
import AlchemistResult from './play/AlchemistResult';
import MatchupBackdrop from './MatchupBackdrop';
import { k, t } from '../localization/t';
import { isFreePlay } from '../utils/freePlay';

// ── CountUp ────────────────────────────────────────────────────
//
// Class component that animates a number from 0 → target over a
// duration, with ease-out cubic. The pop callback fires once when the
// final value is reached so the parent can flash the row.

class CountUp extends Component {
  constructor(props) {
    super(props);
    this.state = { value: 0 };
    this.startTime = 0;
    this.rafId = null;
  }

  componentDidMount() {
    if (this.props.delay > 0) {
      this.delayTimer = setTimeout(() => this.tick(), this.props.delay);
    } else {
      this.tick();
    }
  }

  componentWillUnmount() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.delayTimer) clearTimeout(this.delayTimer);
  }

  tick = () => {
    if (!this.startTime) this.startTime = performance.now();
    const elapsed = performance.now() - this.startTime;
    const duration = this.props.duration || 900;
    const t = Math.min(1, elapsed / duration);
    // Ease-out cubic. Fast at first, settles into the final value.
    const eased = 1 - Math.pow(1 - t, 3);
    const next = Math.round((this.props.target || 0) * eased);

    this.setState({ value: next });

    if (t < 1) {
      this.rafId = requestAnimationFrame(this.tick);
    } else if (this.props.onComplete) {
      this.props.onComplete();
    }
  };

  render() {
    return <span>{this.state.value}</span>;
  }
}

// ── Legend Point gauge ─────────────────────────────────────────
//
// Animates from the LP the player held BEFORE this match to the LP they hold
// now, so the bar itself tells the story the numbers only state. A promotion
// runs the bar to full, flashes, then refills from zero into the new tier; a
// demotion drains to zero and refills backwards from full. Division notches
// at 25/50/75 (same treatment as the hub's ranked bar) keep progress spatial.

const LP_FILL_MS = 1400;
const LP_START_DELAY = 1300;

class LpGauge extends Component {
  constructor(props) {
    super(props);
    const { lp, lpDelta } = props;
    // Where the bar sat BEFORE the match. The gauge holds here long enough
    // to be read, so the fill that follows is understood as this match's
    // contribution rather than just the current total.
    const before = Math.min(100, Math.max(0, (lp || 0) - (lpDelta || 0)));
    this.state = { origin: before, pct: before, stage: 'idle' };
  }

  componentDidMount() {
    const { lpDelta, promoted, demoted } = this.props;
    this.timers = [];
    const at = (ms, fn) => this.timers.push(setTimeout(fn, ms));
    if (!lpDelta) return;
    if (promoted || demoted) {
      const edge = promoted ? 100 : 0;
      at(LP_START_DELAY, () => this.setState({ pct: edge, stage: 'run' }));
      at(LP_START_DELAY + LP_FILL_MS, () => this.setState({ stage: 'flash' }));
      // Cross the boundary with no transition, then fill again inside the
      // new tier so the second leg reads as a fresh bar, not a rewind.
      at(LP_START_DELAY + LP_FILL_MS + 320, () => this.setState({ origin: promoted ? 0 : 100, pct: promoted ? 0 : 100, stage: 'snap' }));
      at(LP_START_DELAY + LP_FILL_MS + 400, () => this.setState({ pct: this.target(), stage: 'run' }));
      at(LP_START_DELAY + LP_FILL_MS * 2 + 400, () => this.setState({ stage: 'done' }));
    } else {
      at(LP_START_DELAY, () => this.setState({ pct: this.target(), stage: 'run' }));
      at(LP_START_DELAY + LP_FILL_MS, () => this.setState({ stage: 'done' }));
    }
  }

  componentWillUnmount() {
    (this.timers || []).forEach(clearTimeout);
  }

  target() {
    return Math.min(100, Math.max(0, this.props.lp || 0));
  }

  render() {
    const { tier, lp, lpDelta } = this.props;
    const { origin, pct, stage } = this.state;
    const hex = TIER_HEX[tier] || '#a8a29e';
    const gaining = (lpDelta || 0) > 0;
    // The band between where the bar was and where it is now. Rendered as
    // its own lit layer so the CHANGE is what catches the eye, not the total.
    const bandLeft = Math.min(origin, pct);
    const bandWidth = Math.abs(pct - origin);
    const move = stage === 'snap' ? 'none' : `width ${LP_FILL_MS}ms cubic-bezier(0.16, 1, 0.3, 1), left ${LP_FILL_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`;
    return (
      <div className="w-full flex items-center gap-2">
        <div
          className="relative flex-1 overflow-hidden"
          style={{
            height: '1.1cqw',
            borderRadius: 'var(--radius-lg)',
            background: 'rgba(0,0,0,0.6)',
            border: `1px solid ${hex}3a`,
            boxShadow: `inset 0 2px 4px rgba(0,0,0,0.6)${stage === 'flash' ? `, 0 0 1.5cqw ${hex}` : ''}`,
            transition: 'box-shadow 200ms ease-out',
          }}
        >
          {/* ONE fill. The bar is a single continuous body: any seam between
              old and new progress reads as a second bar stacked on the first. */}
          <div
            className="absolute inset-y-0 left-0"
            style={{
              width: `${pct}%`,
              borderRadius: 'var(--radius-lg)',
              background: `linear-gradient(90deg, ${hex}66 0%, ${hex} 100%)`,
              boxShadow: `0 0 0.625cqw ${hex}88, inset 0 1px 0 rgba(255,255,255,0.35)`,
              transition: move,
            }}
          />
          {/* This match's swing, marked by LIGHT on the fill. Gains only: on a
              loss the ground is GONE, so lighting up the stretch the bar just
              gave back reads as a prize. The drain itself carries the news. */}
          {gaining && bandWidth > 0.5 ? (
            <div
              className="absolute inset-y-0 match-result-lp-gain"
              style={{
                left: `${bandLeft}%`,
                width: `${bandWidth}%`,
                '--lp-hex': hex,
                transition: move,
              }}
            />
          ) : null}
          {[25, 50, 75].map((x) => (
            <div
              key={x}
              aria-hidden
              className="absolute inset-y-0"
              style={{ left: `${x}%`, width: '1.5px', background: 'rgba(0,0,0,0.6)' }}
            />
          ))}
        </div>
        <span className="text-[0.75cqw] tabular-nums shrink-0" style={{ color: `${hex}cc` }}>
          {Math.min(100, Math.max(0, lp || 0))}/100
        </span>
        {lpDelta ? (
          <span
            className="text-[0.8125cqw] font-bold tabular-nums shrink-0"
            style={{ color: gaining ? '#7dd3fc' : '#e89090' }}
          >
            {gaining ? '+' : ''}{lpDelta}
          </span>
        ) : null}
      </div>
    );
  }
}

// ── Reward row ─────────────────────────────────────────────────

const REWARD_DEFS = [
  {
    key: 'coins', label: k('Gold'), icon: <CoinIcon size={26} />, color: COIN_COLOR,
    tip: k('Buys packs and cosmetics in the Store.'),
  },
  {
    key: 'arcanaShards', label: k('Arcana'), icon: <ShardIcon size={15} />, color: '#8ee7ff',
    tip: k('Crafts a specific card in Spellcraft.'),
  },
  {
    key: 'xp', label: k('Experience'), icon: <IconLaurels size={22} />, color: ACCENT_GOLD,
    tip: k('Levels your account. Each level, one skill point.'),
  },
  {
    key: 'seasonXp', label: k('Season Pass XP'), icon: <IconSeasonScroll size={22} />, color: '#c39df0',
    tip: k('Unlocks tiers on the Arcane Trials pass.'),
  },
];

class RewardRow extends Component {
  constructor(props) {
    super(props);
    this.state = { popping: false };
  }

  handleComplete = () => {
    this.setState({ popping: true });
    setTimeout(() => this.setState({ popping: false }), 520);
  };

  render() {
    const { icon, label, color, value, delay, tip } = this.props;
    const row = (
      <div
        className="match-result-row match-result-reward-row flex items-center justify-between px-3 py-1.5"
        style={{
          animationDelay: `${delay}ms`,
          // Concentric with the plate: its rounded-lg minus its p-1.5 inset.
          borderRadius: 'calc(var(--radius-lg) - var(--spacing) * 1.5)',
        }}
      >
        <div className="flex items-center gap-3">
          <span
            className={cn('text-xl', this.state.popping && 'match-result-row-pop')}
            style={{
              color,
              textShadow: `0 0 12px ${color}88, 0 0 4px ${color}`,
              display: 'inline-block',
            }}
          >
            {icon}
          </span>
          <span className="text-sm font-medium" style={{ color: TEXT_BODY }}>
            {label}
          </span>
        </div>
        <span
          className="text-lg font-bold tabular-nums"
          style={{
            color,
            textShadow: `0 0 10px ${color}66`,
          }}
        >
          +<CountUp target={value} duration={950} delay={delay + 80} onComplete={this.handleComplete} />
        </span>
      </div>
    );
    if (!tip) return row;
    const content = (
      <span className="flex items-center gap-1.5">
        <span style={{ color, display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
        <span>{tip}</span>
      </span>
    );
    return (
      <Tooltip content={content} side="top" delay={120}>
        {row}
      </Tooltip>
    );
  }
}

// ── Victory particles ──────────────────────────────────────────
//
// Sixteen gold flecks rising from the bottom of the panel for the
// victory case. Pure CSS animation, no JS per frame.

// ── ArenaMatchResult ───────────────────────────────────────────


export default class ArenaMatchResult extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // 'reporting' = waiting on the server to settle this match.
      // 'rewards'   = envelope in hand, render the tally.
      // Sandbox / skip path lands straight in 'rewards'.
      phase: 'reporting',
      // True once a report POST is in flight. Used by renderReporting
      // to swap the manual "Who won?" picker for the "Tallying
      // Rewards" copy as soon as a button is clicked, without
      // waiting for the server response to arrive.
      submitting: false,
      // Flipped by handleDisputeReprompt when the server clears both
      // "I won" reports and asks for a re-pick. Forces the picker
      // even if autoWinner was set so the player can concede.
      disputeReprompted: false,
      myReward: null,
      claimError: null,
    };
    this._reportFired = false;
  }

  componentDidMount() {
    const { autoWinner, skipRewardClaim, onSettlementSubscribe, onDisputeRepromptSubscribe, initialEnvelope } = this.props;

    // Hub re-show (a returning dropper): the match already settled while
    // this player was offline and the server re-delivered the envelope on
    // reconnect. Render the tally straight from it. No report POST, no
    // settlement subscription, no "Who won?" picker (autoWinner keeps the
    // pre-apply frame on "Tallying Rewards").
    if (initialEnvelope) {
      this.applyEnvelope(initialEnvelope);
      return;
    }

    // Sandbox / solo / local PvP. No server interaction. Synth a
    // zero-reward envelope so the modal renders VICTORY/DEFEAT + the
    // duration line and lets the player close out.
    if (skipRewardClaim && (autoWinner === 'me' || autoWinner === 'opponent')) {
      // No server reward flow here (friendly / non-ranked match), but we
      // still capture the match log for card analytics. Upload is fully
      // decoupled from reward reporting.
      this.uploadLogIfMeaningful();
      this.setState({
        phase: 'rewards',
        myReward: {
          mode: this.props.matchup?.solo ? 'solo' : 'sandbox',
          won: autoWinner === 'me',
          coins: 0, xp: 0, seasonXp: 0, arcanaShards: 0,
          durationMinutes: this.props.matchDurationMinutes || 0,
          newTotals: null,
        },
      }, this.playResultSound);
      return;
    }

    // Subscribe to `match:settled` first so a settlement that lands
    // before our own POST returns (the opponent reported a few ms
    // earlier and the server fanned out) is not missed.
    if (onSettlementSubscribe) {
      this._unsubSettled = onSettlementSubscribe((envelope) => this.applyEnvelope(envelope));
    }
    if (onDisputeRepromptSubscribe) {
      this._unsubReprompt = onDisputeRepromptSubscribe(() => this.handleDisputeReprompt());
    }

    if (autoWinner === 'me' || autoWinner === 'opponent') {
      this.submitReport(autoWinner === 'me');
    }
  }

  componentWillUnmount() {
    this._unsubSettled?.();
    this._unsubReprompt?.();
  }

  // Server cleared both "I won" reports and is asking each client to
  // pick again. Reset to the picker. Clear `_reportFired` so the
  // next click resubmits, and flag `disputeReprompted` so the picker
  // shows even if autoWinner was set (engine-determined wins are
  // overridable here so the player can concede out of the loop).
  handleDisputeReprompt = () => {
    if (this.state.phase === 'rewards') return;
    this._reportFired = false;
    this.setState({
      phase: 'reporting',
      submitting: false,
      disputeReprompted: true,
      myReward: null,
      claimError: null,
    });
  };

  // Plays the dedicated match-result fanfare. Uses the same volume
  // pipeline as every other game sound so the SFX slider applies.
  playResultSound = () => {
    playUI(UI.CONFIRM);
  };

  // Upload the captured match log for card analytics. Fire-and-forget and
  // fully decoupled from reward reporting. A log failure must never block
  // or delay the result flow (uploadMatchLog queues for retry on its own).
  // Guarded so it only fires once and only for "meaningful" games.
  // Replay is served from the uploaded log, so wait for the POST to land
  // before handing off. Exit + open happen in the app.
  watchReplay = async () => {
    const { roomCode, onWatchReplay } = this.props;
    if (!roomCode || !onWatchReplay || this.state.replayLoading) return;
    this.setState({ replayLoading: true });
    try { await this._logUpload; } catch { /* fall through; the fetch reports */ }
    onWatchReplay(roomCode);
  };

  uploadLogIfMeaningful = () => {
    if (this._logUploaded) return;
    this._logUploaded = true;
    try {
      const log = this.props.getMatchLog && this.props.getMatchLog();
      if (log && Array.isArray(log.events) && log.events.length >= 5) {
        this._logUpload = uploadMatchLog(log).catch(() => null);
      }
    } catch { /* logging must never affect the result flow */ }
  };

  // Submit this client's outcome to the server's arbitration endpoint.
  // Idempotent: if the server already has both reports the response
  // carries the envelope; otherwise we wait for `match:settled` via WS.
  submitReport = async (iWon) => {
    if (this._reportFired) return;
    this._reportFired = true;
    this.setState({ submitting: true });

    const { roomCode, deckElement, deckElements, avatarId } = this.props;
    if (!roomCode) {
      console.error('[ArenaMatchResult] roomCode missing. Cannot report outcome');
      this.applyEnvelope(this.zeroEnvelope(iWon, 'no_room_code'), t('Match window expired.'));
      return;
    }

    try {
      const result = await reportMatchOutcome({
        roomCode,
        won: iWon,
        reason: 'engine_resolved',
        deckElement: deckElement || null,
        deckElements: deckElements || [],
        avatarId: avatarId || null,
      });
      if (result.status === 'settled' || result.status === 'cached') {
        this.applyEnvelope(result.envelope);
      }
      // 'pending' → just wait. The match:settled WS push will fire
      // applyEnvelope via the subscription wired in componentDidMount.

      // Fire-and-forget the match log AFTER the reward report.
      this.uploadLogIfMeaningful();
    } catch (err) {
      console.error('[ArenaMatchResult] reportMatchOutcome failed:', err);
      this.applyEnvelope(this.zeroEnvelope(iWon, 'report_failed'), err?.message || t('Could not contact the server.'));
    }
  };

  zeroEnvelope = (iWon, reason) => ({
    mode: 'ranked',
    coinsEarned: 0, xpEarned: 0, seasonXpEarned: 0, arcanaShardsEarned: 0,
    durationMinutes: this.props.matchDurationMinutes || 0,
    won: iWon,
    newTotals: null,
    lpDelta: 0,
    promoted: false, tierPromoted: false, demoted: false, tierDemoted: false, shielded: false,
    arbitration: { reason },
  });

  /**
   * Render a server-supplied envelope (the shape returned by
   * /matches/:roomCode/report). Idempotent. Re-entry while already
   * in 'rewards' is a no-op so a duplicate `match:settled` push or
   * a race between the POST return and the WS push can't double-pop.
   */
  applyEnvelope = (envelope, errorOverride = null) => {
    if (this.state.phase === 'rewards') return;
    if (!envelope) return;

    const reward = {
      mode: envelope.mode || 'ranked',
      coins: envelope.coinsEarned || 0,
      xp: envelope.xpEarned || 0,
      seasonXp: envelope.seasonXpEarned || 0,
      arcanaShards: envelope.arcanaShardsEarned || 0,
      durationMinutes: envelope.durationMinutes ?? 0,
      won: !!envelope.won,
      newTotals: envelope.newTotals || null,
      // Level-up payload the parent forwards into the celebration overlay.
      levelsGained: envelope.levelsGained || 0,
      levelUpCoins: envelope.levelUpCoins || 0,
      // Ladder metadata.
      lpDelta: envelope.lpDelta || 0,
      promoted: !!envelope.promoted,
      tierPromoted: !!envelope.tierPromoted,
      demoted: !!envelope.demoted,
      tierDemoted: !!envelope.tierDemoted,
      shielded: !!envelope.shielded,
      // Season + Glicko + cap state. Same shape as the legacy claim
      // response so existing render code in renderRewards keeps working.
      seasonRanked: envelope.seasonRanked || null,
      glicko: envelope.glicko || null,
      competitiveCapped: !!envelope.competitiveCapped,
      competitiveCredited: !!envelope.competitiveCredited,
      arbitration: envelope.arbitration || null,
      // The Oracle's settled reading, when the player took one.
      oracle: envelope.oracle || null,
      // The Battlemage's settled standing: stacks, multiplier, Ferocity.
      battlemage: envelope.battlemage || null,
      // The Alchemist's harvest: humours, potions spent, a drop.
      alchemist: envelope.alchemist || null,
    };

    // Network-level error from the report POST is the only arbitration
    // signal that still produces a zero envelope. Disputes loop via
    // match:dispute_reprompt instead of settling.
    const claimError = errorOverride;

    this.setState({ phase: 'rewards', myReward: reward, claimError }, this.playResultSound);

    if (this.props.onRewardsApplied) {
      this.props.onRewardsApplied(reward);
    }
  };

  // ── Phase renderers ────────────────────────────────────────

  renderReporting() {
    const { disputeReprompted } = this.state;
    const autoSubmitted = !disputeReprompted && (this.props.autoWinner === 'me' || this.props.autoWinner === 'opponent');
    const awaitingServer = this.state.submitting || autoSubmitted;

    // No engine-determined winner yet. Let the player declare it.
    // Used when "End Match" is clicked manually before a death blow
    // has resolved. The chosen outcome is the same submitReport
    // call the auto path uses, so the server arbitration treats
    // both cases identically.
    if (!awaitingServer) {
      return (
        <>
          <h2 className="text-xl font-bold text-center mb-2 arena-heading" style={{ color: TEXT_PRIMARY }}>
            {t('Match Complete')}
          </h2>
          {disputeReprompted ? (
            <p className="text-sm text-center mb-3" style={{ color: '#e89090' }}>
              {t('You both reported the same outcome. Pick again so the match can settle.')}
            </p>
          ) : null}
          <p className="text-sm text-center mb-6" style={{ color: TEXT_MUTED }}>
            {t('Who won this match?')}
          </p>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              className="match-result-btn match-result-btn-win rounded-xl px-4 py-3 text-sm font-semibold"
              onClick={() => this.submitReport(true)}
            >
              {t('I won')}
            </button>
            <button
              type="button"
              className="match-result-btn match-result-btn-loss rounded-xl px-4 py-3 text-sm font-semibold"
              onClick={() => this.submitReport(false)}
            >
              {t('My opponent won')}
            </button>
          </div>
        </>
      );
    }

    return (
      <>
        <h2 className="text-xl font-bold text-center mb-2 arena-heading" style={{ color: TEXT_PRIMARY }}>
          {isFreePlay() ? t('Recording Result') : t('Tallying Rewards')}
        </h2>
        <p className="text-sm text-center" style={{ color: TEXT_MUTED }}>
          {t('Confirming the result with the server…')}
        </p>
      </>
    );
  }

  renderActions() {
    const { replayLoading } = this.state;
    const canReplay = !!(this.props.roomCode && this.props.onWatchReplay);
    return (
      <div className="flex gap-3 mx-auto" style={{ width: '30cqw' }}>
        {canReplay ? (
          <button
            type="button"
            className="match-result-btn match-result-btn-secondary flex-1 rounded-xl px-4 py-3 text-sm font-bold"
            disabled={replayLoading}
            onClick={this.watchReplay}
          >
            {replayLoading ? t('Loading replay…') : t('Watch replay')}
          </button>
        ) : null}
        <button
          type="button"
          className="match-result-btn match-result-btn-primary flex-1 rounded-xl px-4 py-3 text-sm font-bold"
          onClick={this.props.onMatchComplete || this.props.onClose}
        >
          {t('Continue')}
        </button>
      </div>
    );
  }

  renderRewards() {
    const { myReward, claimError } = this.state;
    const { roundsPlayed, matchup } = this.props;
    // A neutral settlement outranks the envelope's `won` flag. The server
    // zeroes `won` for both seats, which is not the same as a defeat.
    const neutral = neutralArbitration(myReward.arbitration?.reason);
    const won = !neutral && myReward.won;
    // Sandbox and solo pay nothing; the reward rows give way to a note.
    const unrewarded = myReward.mode === 'sandbox' || myReward.mode === 'solo';
    // In a solo match the player piloted both seats, so name the winning
    // seat instead of calling it a victory. Older servers settle solo as
    // 'sandbox', so the matchup flag decides, not the mode.
    const solo = unrewarded && matchup?.solo;
    // Free play pays nothing and has no classes: no reward rows, no class
    // plates. The ladder and valor rows below still apply to ranked.
    const freePlay = isFreePlay();
    const rewardRowCount = freePlay ? 0 : REWARD_DEFS.length;
    const banner = neutral ? neutral.banner
      : solo ? t('{name} wins', { name: won ? matchup.localName : matchup.remoteName })
      : won ? t('Victory') : t('Defeat');
    const titleColor = neutral ? '#b9c2cf' : won ? '#f5d782' : '#e89090';
    const subtitleColor = neutral
      ? 'rgba(185, 194, 207, 0.85)'
      : won ? GOLD_TEXT + ' 0.85)' : 'rgba(232, 144, 144, 0.85)';

    // Stagger the rows so each one slides in after the previous.
    // First row reveals at 600ms (after the banner has settled).
    const ROW_BASE_DELAY = 600;
    const ROW_STAGGER = 180;

    return (
      <div className="relative">
        <div className="relative mx-auto flex flex-col" style={{ width: '30cqw' }}>
          {/* Big banner */}
          <h2
            className={cn(
              'text-center font-bold tracking-wider arena-heading',
              neutral ? 'match-result-banner-neutral'
                : won ? 'match-result-banner-victory' : 'match-result-banner-defeat',
            )}
            style={{
              fontSize: '2.875cqw',
              lineHeight: 1.05,
              color: titleColor,
              marginTop: 0,
              marginBottom: '0.25cqw',
            }}
          >
            {banner}
          </h2>

          {/* Stats row */}
          <div className="flex items-center justify-center gap-3 mb-2" style={{ color: subtitleColor }}>
            <div className="flex items-center gap-1.5">
              <span className="text-[0.6875cqw] opacity-70">{t('Duration')}</span>
              <span className="text-sm font-semibold tabular-nums">
                {t('{m}m', { m: myReward.durationMinutes ?? 0 })}
              </span>
            </div>
            <span className="opacity-30">·</span>
            <div className="flex items-center gap-1.5">
              <span className="text-[0.6875cqw] opacity-70">{t('Rounds')}</span>
              <span className="text-sm font-semibold tabular-nums">
                {roundsPlayed ?? EM_DASH}
              </span>
            </div>
          </div>

          {/* Animated divider */}
          <div className="match-result-divider mb-3 mx-auto" style={{
            height: 1,
            width: '70%',
            background: `linear-gradient(90deg, transparent, ${GOLD} 0.5), transparent)`,
          }} />

          {/* Reward rows. Hidden entirely for sandbox / solo matches and for
              neutral settlements, which award nothing. The reward panel is
              replaced with a short explanation so the player understands why
              no coins / XP / shards animated in. */}
          {freePlay && !neutral ? (
            myReward.mode === 'casual' && !unrewarded ? (
              <div className="text-center mb-3 text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>
                {t('Casual match. No Legend Points or rank change.')}
              </div>
            ) : null
          ) : unrewarded && !neutral ? null : neutral || unrewarded ? (
            <div
              className="match-result-plate text-center mb-5 px-4 py-3 rounded-md"
              style={{
                color: TEXT_BODY,
                fontSize: '0.8125cqw',
                lineHeight: 1.5,
              }}
            >
              {neutral ? neutral.body : t('Sandbox match. No gold, XP, shards, or season progress earned.')}
            </div>
          ) : (
            <div className="match-result-plate flex flex-col gap-1.5 mb-3 rounded-lg p-1.5">
              {REWARD_DEFS.map((def, i) => (
                <RewardRow
                  key={def.key}
                  icon={def.icon}
                  label={t(def.label)}
                  color={def.color}
                  value={myReward[def.key] || 0}
                  tip={t(def.tip)}
                  delay={ROW_BASE_DELAY + i * ROW_STAGGER}
                />
              ))}
              {/* Casual pays exactly what ranked pays; only the ladder
                  sits it out. Saying so here closes the loop on the
                  choice made in the setup dialog. */}
              {myReward.mode === 'casual' ? (
                <div className="text-center mt-1 text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>
                  {t('Casual match. Full rewards, no Legend Points or rank change.')}
                </div>
              ) : null}
            </div>
          )}

          {!freePlay && !unrewarded && !neutral && myReward.oracle ? <OracleResult oracle={myReward.oracle} /> : null}
          {!freePlay && !unrewarded && !neutral && myReward.battlemage ? <BattlemageResult battlemage={myReward.battlemage} /> : null}
          {!freePlay && !unrewarded && !neutral && myReward.alchemist ? <AlchemistResult alchemist={myReward.alchemist} /> : null}

          {/* Ranked ladder update. Appears under the reward rows so the
              promotion fanfare lands after the count-up animations.
              Suppressed for casual: the envelope still carries the
              player's unchanged rank, and rendering a "0 LP" ladder
              panel under a casual result is exactly the ambiguity the
              mode exists to remove. */}
          {myReward.mode !== 'casual' && myReward.newTotals?.rank ? (() => {
            const rank = myReward.newTotals.rank;
            const lpDelta = myReward.lpDelta || 0;

            // Headline message describing what happened to the rank.
            let headline = null;
            let headlineColor = TEXT_BODY;
            if (myReward.competitiveCapped) {
              headline = t('Ranked progress paused. Too many ranked matches this hour');
              headlineColor = TEXT_MUTED;
            } else if (myReward.tierPromoted) {
              headline = t('Promoted to {rank}!', { rank: formatRank(rank.tier, rank.division) });
              headlineColor = ACCENT_GOLD;
            } else if (myReward.promoted) {
              headline = t('Promoted to {rank}', { rank: formatRank(rank.tier, rank.division) });
              headlineColor = ACCENT_GOLD;
            } else if (myReward.tierDemoted) {
              headline = t('Demoted to {rank}', { rank: formatRank(rank.tier, rank.division) });
              headlineColor = '#e89090';
            } else if (myReward.demoted) {
              headline = t('Demoted to {rank}', { rank: formatRank(rank.tier, rank.division) });
              headlineColor = '#e89090';
            } else if (myReward.shielded) {
              const left = myReward.newTotals.shieldGamesLeft ?? 0;
              headline = left === 1 ? t('Promotion shield held. 1 game left') : t('Promotion shield held. {n} games left', { n: left });
              headlineColor = '#7dd3fc';
            }

            return (
              <div
                className="match-result-row match-result-plate flex flex-col items-center justify-center px-4 py-2.5 rounded-lg mb-2"
                style={{
                  borderColor: myReward.tierPromoted ? ACCENT_GOLD : undefined,
                  animationDelay: `${ROW_BASE_DELAY + rewardRowCount * ROW_STAGGER}ms`,
                  boxShadow: myReward.tierPromoted
                    ? `0 0 24px ${ACCENT_GOLD}55, inset 0 0 16px ${ACCENT_GOLD}22`
                    : 'none',
                }}
              >
                {headline ? (
                  <div className="text-xs font-bold mb-1" style={{ color: headlineColor }}>
                    {headline}
                  </div>
                ) : null}
                <div className="w-full flex items-center justify-center mb-1">
                  <span className="text-sm font-semibold" style={{ color: TEXT_PRIMARY }}>
                    {formatRank(rank.tier, rank.division)}
                  </span>
                </div>
                <LpGauge
                  tier={rank.tier}
                  lp={rank.lp}
                  lpDelta={lpDelta}
                  promoted={!!(myReward.promoted || myReward.tierPromoted)}
                  demoted={!!(myReward.demoted || myReward.tierDemoted)}
                />
              </div>
            );
          })() : null}

          {/* Ranked Season points change. Sits under the all-time
              ladder update so the player sees both the lifetime
              ladder movement and the bi-weekly seasonal race delta
              in one glance. Only renders for ranked matches that
              actually credited (skips sandbox + sub-floor durations). */}
          {myReward.seasonRanked ? (() => {
            const delta = myReward.seasonRanked.pointsDelta || 0;
            const positive = delta > 0;
            const color = positive ? '#7dd3fc' : delta < 0 ? '#e89090' : TEXT_MUTED;
            // Ghost row, same shape as the reward rows. The season total and
            // match count belong on the season screen; here only this match's
            // valor matters.
            return (
              <Tooltip
                side="top"
                delay={120}
                content={(
                  <span className="flex items-center gap-1.5">
                    <span style={{ color, display: 'inline-flex', alignItems: 'center' }}><IconSwords size={16} /></span>
                    <span>{t('Ranks you on this season’s valor leaderboard.')}</span>
                  </span>
                )}
              >
                <div
                  className="match-result-row match-result-reward-row flex items-center justify-between px-3 py-1.5 mb-2"
                  style={{
                    animationDelay: `${ROW_BASE_DELAY + (rewardRowCount + 1) * ROW_STAGGER}ms`,
                    borderRadius: 'calc(var(--radius-lg) - var(--spacing) * 1.5)',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <span style={{ color, display: 'inline-flex', textShadow: `0 0 12px ${color}88` }}>
                      <IconSwords size={22} />
                    </span>
                    <span className="text-sm font-medium" style={{ color: TEXT_BODY }}>{t('Valor')}</span>
                  </div>
                  <span
                    className="text-lg font-bold tabular-nums"
                    style={{ color, textShadow: `0 0 10px ${color}66` }}
                  >
                    {positive ? '+' : ''}{delta}
                  </span>
                </div>
              </Tooltip>
            );
          })() : null}

          {claimError ? (
            <p className="text-xs text-center mb-3" style={{ color: '#e89090' }}>
              {claimError}
            </p>
          ) : null}

        </div>
      </div>
    );
  }

  render() {
    const { phase, myReward } = this.state;
    const isRewards = phase === 'rewards';
    const neutral = isRewards ? neutralArbitration(myReward.arbitration?.reason) : null;
    const won = isRewards && !neutral && !!myReward.won;

    return (
      <div className="fixed inset-0 z-[1200] flex items-center justify-center" style={{
        background: 'rgba(0, 0, 0, 0.78)',
        backdropFilter: 'blur(6px)',
      }}>
        <div
          className={cn(
            'match-result-card match-result-frame relative w-[58cqw] h-[35cqw] rounded-2xl overflow-hidden',
            isRewards && won ? 'match-result-frame-victory'
              : isRewards && !neutral ? 'match-result-frame-defeat' : null,
          )}
          style={{ background: PANEL_BG, isolation: 'isolate' }}
        >
          {isRewards ? <MatchupBackdrop matchup={this.props.matchup || null} won={won} neutral={!!neutral} /> : null}
          {isRewards && won ? <VictoryEmbers /> : null}
          <div className="relative h-full p-6 flex flex-col">
            <FourCorners knots />
            {phase === 'reporting' ? (
              <div className="m-auto" style={{ width: '30cqw' }}>{this.renderReporting()}</div>
            ) : null}
            {isRewards ? this.renderRewards() : null}
            {isRewards ? <div className="mt-auto pt-4">{this.renderActions()}</div> : null}
          </div>
        </div>
      </div>
    );
  }
}
